import { LazyMotion, m } from 'framer-motion'
import styled from 'styled-components'
import { CssUnit } from '@/types'
import { toUnit } from '@/utils/styles'
import useMotionSafe from './use-motion-safe'

const loadFeatures = () => import('./features.js').then(res => res.default)

type RevealProps = {
  isOpen?: boolean
  width?: CssUnit
  children: React.ReactNode
  height?: CssUnit
  closedHeight?: CssUnit
  noTransform?: boolean
}

type Custom = {
  height: CssUnit
  closedHeight: CssUnit
}

const Container = styled(m.div)<{ $width: CssUnit }>`
  overflow: hidden;
  width: ${props => toUnit(props.$width)};
  display: block;
`

const hasHeight = (closedHeight: CssUnit) => typeof closedHeight === 'string' || closedHeight > 0

const variants = {
  open: (custom: Custom) => {
    const { closedHeight, height } = custom
    const notHidden = hasHeight(closedHeight)
    return {
      opacity: 1,
      height: height ? toUnit(height) : 'auto',
      ...(!notHidden ? { display: 'block' } : {})
    }
  },
  closed: (custom: Custom) => {
    const { closedHeight } = custom
    const displayNone = { transitionEnd: { display: 'none' } }
    const notHidden = hasHeight(closedHeight)
    return {
      opacity: notHidden ? 1 : 0,
      height: notHidden ? toUnit(closedHeight) : 0,
      ...(!notHidden ? displayNone : {})
    }
  }
}

const Reveal: React.FC<RevealProps> = ({
  isOpen,
  children,
  height = 'auto',
  width = 'unset',
  closedHeight = 0,
  noTransform = false,
  ...rest
}) => {
  const notHidden = hasHeight(closedHeight)
  const motionSafe = useMotionSafe()
  const extra = noTransform ? { layout: false } : {}
  return (
    <LazyMotion features={loadFeatures}>
      <Container
        $width={width}
        custom={{ height, closedHeight }}
        layout
        initial={isOpen ? 'open' : 'closed'}
        animate={isOpen ? 'open' : 'closed'}
        inherit={false}
        transition={motionSafe({ type: 'spring', stiffness: 100, damping: 20, mass: 1 })}
        variants={variants}
        aria-expanded={isOpen}
        aria-hidden={!notHidden && !isOpen}
        {...extra}
        {...rest}
      >
        {children}
      </Container>
    </LazyMotion>
  )
}

export default Reveal
